import { useEffect, useRef, useState } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import {
  disablePush,
  enablePush,
  getPushStatus,
  isPushSupported,
  type PushStatus,
} from '@/lib/pushClient';

type UiStatus = PushStatus | 'loading';

function statusLabel(s: UiStatus): string {
  switch (s) {
    case 'loading':
      return 'Checking…';
    case 'unsupported':
      return 'Not supported on this device.';
    case 'denied':
      return 'Blocked in your browser settings.';
    case 'not-configured':
      return 'Push is not configured on the server.';
    case 'subscribed':
      return 'Notifications are enabled on this device.';
    case 'idle':
      return 'Not receiving notifications on this device.';
  }
}

export default function PlayerHubPushButton({ token }: { token: string }) {
  const [status, setStatus] = useState<UiStatus>('loading');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const refresh = () => {
    getPushStatus()
      .then((s) => setStatus(s))
      .catch(() => setStatus('idle'));
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isPushSupported()) return null;

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await enablePush({ kind: 'player', token });
      setStatus(next);
      if (next === 'subscribed') {
        // Clear any prior local dismissal so the auto-prompt reappears if lost again.
        try {
          window.localStorage.removeItem(`push-prompt-dismissed-at:${token}`);
        } catch {
          // ignore
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await disablePush({ kind: 'player', token });
      setStatus(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const iconColor =
    status === 'subscribed'
      ? 'text-emerald-300'
      : status === 'denied'
        ? 'text-red-400'
        : 'text-slate-300';
  const Icon = status === 'subscribed' ? Bell : BellOff;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-800 bg-slate-950 hover:text-slate-100 ${iconColor}`}
        title="Notifications"
        aria-label="Notification settings"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 w-64 rounded-md border border-slate-700 bg-slate-900 p-3 text-sm shadow-lg"
        >
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
            <Bell size={12} />
            Notifications
          </div>
          <div className="mb-2 text-xs text-slate-300">{statusLabel(status)}</div>
          {status === 'denied' && (
            <div className="mb-2 text-[11px] text-slate-500">
              Enable notifications in your browser/site settings, then tap Re-request.
            </div>
          )}
          {error && <div className="mb-2 text-xs text-red-400">{error}</div>}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={busy || status === 'unsupported' || status === 'not-configured'}
              onClick={enable}
              className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {status === 'subscribed' ? 'Re-request on this device' : 'Enable notifications'}
            </button>
            {status === 'subscribed' && (
              <button
                type="button"
                disabled={busy}
                onClick={disable}
                className="rounded border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"
              >
                Disable on this device
              </button>
            )}
            <button
              type="button"
              onClick={refresh}
              className="text-[11px] text-slate-500 underline hover:text-slate-300"
            >
              Refresh status
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
